<template>
  <div>
    <h6>
      {{ question.label }} {{ question.required ? '*' : '' }}
      <small v-if="isLockable" class="text-muted">
        <i class="fa fa-fw fa-lock"></i>
      </small>
      <small v-if="isEditable">
        <b-button v-if="isEditable" variant="link" size="sm" class="text-danger py-0" @click="editing = !editing">
          {{ trans(!editing ? 'edit' : 'save') }}
        </b-button>
      </small>
    </h6>
    <div class="mb-2" v-html="question.description"></div>
    <b-row v-for="(v, index) in values" :key="index">
      <b-col v-if="control == 'date' || control == 'datetime'" class="col-12 col-sm-5">
        <b-form-group>
          <datetime
            v-model="v.start_date"
            :config="dateOptions"
            :name="`${question.slug}.start_date`"
            :required="question.required"
            :state="errors && !!errors[index] ? false : null"
            :placeholder="trans('start')"
            :readonly="isLocked"
            :class="`form-control form-control-${size} ${!!errors && !!errors[index] ? 'is-invalid' : (!!v.start_date ? 'is-valid' : '')}`"
            @dp-show="$emit('clear-error', question.slug)"
          />
          <b-form-invalid-feedback :state="!!errors && !!errors[index] ? false : null">
            {{ !!errors && !!errors[index] ? errors[index] : '' }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col v-if="control == 'time' || control == 'datetime'" class="col-12 col-sm-5">
        <b-form-group>
          <datetime
            v-model="v.start_time"
            :config="timeOptions"
            :name="`${question.slug}.start_time`"
            :required="question.required"
            :state="errors && !!errors[index].start_time ? false : null"
            :placeholder="trans(control == 'datetime' ? 'time' : 'start')"
            :readonly="isLocked"
            :class="`form-control form-control-${size} ${!!errors && !!errors[index].start_time ? 'is-invalid' : (!!v.start_time ? 'is-valid' : '')}`"
            @dp-show="$emit('clear-error', question.slug)"
          />
          <b-form-invalid-feedback v-if="control != 'datetime'" :state="!!errors && !!errors[index].start_time ? false : null">
            {{ !!errors && !!errors[index].start_time ? errors[index].start_time : '' }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col v-if="control == 'date' || control == 'datetime'" class="col-12 col-sm-5">
        <b-form-group>
          <datetime
            v-model="v.end_date"
            :config="dateOptions"
            :name="`${question.slug}.end_date`"
            :required="question.required"
            :state="errors && !!errors[index] ? false : null"
            :placeholder="trans('end')"
            :readonly="isLocked"
            :class="`form-control form-control-${size} ${!!errors && !!errors[index] ? 'is-invalid' : (!!v.end_date ? 'is-valid' : '')}`"
            @dp-show="$emit('clear-error', question.slug)"
          />
          <b-form-invalid-feedback :state="!!errors && !!errors[index] ? false : null">
            {{ !!errors && !!errors[index] ? errors[index] : '' }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col v-if="control == 'time' || control == 'datetime'" class="col-12 col-sm-5">
        <b-form-group>
          <datetime
            v-model="v.end_time"
            :config="timeOptions"
            :name="`${question.slug}.end_time`"
            :required="question.required"
            :state="errors && !!errors[index] ? false : null"
            :placeholder="trans(control == 'datetime' ? 'time' : 'end')"
            :readonly="isLocked"
            :class="`form-control form-control-${size} ${!!errors && !!errors[index].end_time ? 'is-invalid' : (!!v.end_time ? 'is-valid' : '')}`"
            @dp-show="$emit('clear-error', question.slug)"
          />
          <b-form-invalid-feedback v-if="control != 'datetime'" :state="!!errors && !!errors[index].end_time ? false : null">
            {{ !!errors && !!errors[index].end_time ? errors[index].end_time : '' }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col v-if="values.length > 1 && !isLocked" class="col-1 pl-0">
        <b-button variant="link" class="text-danger" @click="onClickRemove(index)">
          <i class="fa fa-fw fa-times"></i>
        </b-button>
      </b-col>
    </b-row>
    <b-form-group v-if="question.options.multiple && !isLocked">
      <b-button variant="link" size="sm" :class="question.visibility == 'admin_only' ? 'text-danger' : ''" @click="onClickAdd">
        <i class="fa fa-fw fa-plus"></i>
        {{ trans('add') }}
      </b-button>
    </b-form-group>
  </div>
</template>

<script>
  import Datetime from 'vue-bootstrap-datetimepicker'

  export default {
    props: {
      organization: Object,
      value: {
        type: [Array, Object], // if date range existed before multiple options was implemented
      },
      question: Object,
      disabled: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: 'md',
      },
      errors: Array,
    },
    components: {
      Datetime,
    },
    data() {
      return {
        trans: trans,
        values: [],
        editing: false,
        testCount: 1
      }
    },
    computed: {
      control() {
        return this.question.options.control
      },
      dateOptions() {
        return {
          locale: window.lang,
          format: 'LL',
          showClear: !this.question.required,
          widgetPositioning: {vertical: 'bottom'},
        }
      },
      timeOptions() {
        return {
          locale: window.lang,
          format: 'LT',
          showClear: !this.question.required,
          widgetPositioning: {vertical: 'bottom'},
        }
      },
      answer() {
        return _.reduce(this.values, (answer, value) => {
          if (value) {
            let start = this.control == 'time' ?
              moment(value.start_time, 'LT') :
              moment(value.start_date, 'LL')
            if (this.control == 'datetime') {
              let time = moment(value.start_time, 'LT')
              start.set({
                hour: time.hour(),
                minute: time.minute(),
                second: 0,
              })
            }
            let end = this.control == 'time' ?
            moment(value.end_time, 'LT') :
            moment(value.end_date, 'LL')
            if (this.control == 'datetime') {
              let time = moment(value.end_time, 'LT')
              end.set({
                hour: time.hour(),
                minute: time.minute(),
                second: 0,
              })
            }
            answer.push({start, end})
          }
          return answer
        }, [])
      },
      isLocked() {
        if (this.question.options.locked && (!this.organization || !this.organization.auth_user)) return true
        return this.question.options.locked && !this.editing
      },
      isLockable() {
        return this.question.options.locked
      },
      isEditable() {
        return this.question.options.locked && !!this.organization && !!this.organization.auth_user
      },
    },
    watch: {
      value() {
        this.values = this.getHydratedValues()
      },
      answer() {
        if (!_.isEqual(this.value, this.answer))
          this.$emit('input', this.answer)
      },
    },
    methods: {
      onClickAdd() {
        this.values.push({
          start_date: null,
          start_time: null,
          end_date: null,
          end_time: null
        })
      },
      onClickRemove(index) {
        this.values.splice(index, 1)
      },
      getHydratedValues() {
        // if value was set before multiple options was implemented, push into array
        let value = _.isArray(this.value) ? _.clone(this.value) : (_.isObject(this.value)) ? [_.clone(this.value)] : [null]

        return _.map(value && value.length > 0 ? value : [null], value => {
          return {
            start_date: value && value.start ? moment(value.start).format('LL') : null,
            start_time: value && value.start ? moment(value.start).format('LT') : null,
            end_date: value && value.end ? moment(value.end).format('LL') : null,
            end_time: value && value.end ? moment(value.end).format('LT') : null
          }
        })
      },
    },
    mounted() {
      window['questionDateRange'+this.question.id] = this,
      this.values = this.getHydratedValues()
    },
  }
</script>